/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * The v2 app frame: a full-height column with the header on top, the
 * sidebar beside a scrolling <main>, and the calling bar along the bottom
 * under both. Only <main> scrolls, so the header and the bar stay put without
 * position: fixed. Pages render inside <main> unchanged: DashboardLayout and
 * DashboardNavbar become pass-throughs under v2.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import useMediaQuery from "@mui/material/useMediaQuery";
import { v2 as t } from "styles/tokens/v2";
import CommandPalette from "./CommandPalette";
import HeaderV2 from "./HeaderV2";
import SidebarV2 from "./SidebarV2";
import SoftphoneBar, { useSoftphoneBar } from "./SoftphoneBar";
import SettingsLayoutV2 from "layouts/crm/settings/SettingsLayoutV2";
import { ScrollContainerProvider } from "./ScrollContainer";
import { activeItemKey, buildSidebarSections, flattenItems } from "./sidebarModel";
import { ActiveAppContext, appForRouteKey, resolveActiveApp } from "./apps";
import useShellPrefs from "./useShellPrefs";
import { useModuleEntitlements } from "hooks/useModuleEntitlements";
import { useTravelUi } from "hooks/useTravelUi";
import { useAuth } from "auth/Auth";
import { usePermissionsContext } from "auth/PermissionsProvider";
import { computePermission } from "auth/permissionRules";
import { availableCreateActions } from "./createActions";

/** Routes the user can open that create something ("/reports/new" ...). */
function createRoutesFrom(routes) {
  const out = [];
  const walk = (list) =>
    (list || []).forEach((r) => {
      if (r?.route && /\/new$/.test(r.route) && !r.route.includes(":")) out.push(r);
      if (Array.isArray(r?.collapse)) walk(r.collapse);
    });
  walk(routes);
  const seen = new Set();
  return out.filter((r) => (seen.has(r.route) ? false : seen.add(r.route)));
}

// Every page under /settings gets the settings menu beside it.
const isSettingsPath = (pathname) => pathname === "/settings" || pathname.startsWith("/settings/");

export default function AppFrame({ sidenavRoutes, allowedRoutes, children }) {
  const narrow = useMediaQuery("(max-width:899.95px)");
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const mainRef = useRef(null);
  const bar = useSoftphoneBar();

  const pages = useMemo(() => flattenItems(buildSidebarSections(sidenavRoutes)), [sidenavRoutes]);

  // Active app: the user's choice, else the tenant default. Opening a page
  // another app owns (from search, a link, a notification) switches to it.
  const { pathname } = useLocation();
  const { apps: entitledApps, defaultApp, appOwnedRouteKeys } = useModuleEntitlements();
  // The sidebar already hides every travel item while the company travel
  // switch is off, so a Travel app would be empty; leave it out until it is on.
  const { showTravel } = useTravelUi();
  const apps = useMemo(
    () => (entitledApps || []).filter((a) => a.code !== "TRAVEL" || showTravel),
    [entitledApps, showTravel]
  );
  const { activeApp: storedApp, setActiveApp } = useShellPrefs();
  const ownedKeys = useMemo(() => new Set(appOwnedRouteKeys || []), [appOwnedRouteKeys]);
  const app = resolveActiveApp(apps, storedApp, defaultApp);
  useEffect(() => {
    const key = activeItemKey(pages, pathname);
    const target = appForRouteKey(apps, ownedKeys, key, app);
    if (target && app && target.code !== app.code) setActiveApp(target.code);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, apps]);
  const appContext = useMemo(
    () => ({ app, apps: apps || [], ownedKeys, setApp: setActiveApp }),
    [app, apps, ownedKeys, setActiveApp]
  );
  // New menu: the priority list pages' own create dialogs (?create=1), then
  // the routes that are creation pages already (/reports/new ...).
  const { user } = useAuth();
  const { snapshot, ready, mode, isSuper } = usePermissionsContext();
  const createRoutes = useMemo(() => {
    const canCreate = (objectApiName) =>
      computePermission({ user, objectApiName, snapshot, ready, mode, isSuper }).canCreate;
    return [
      ...availableCreateActions({ routes: allowedRoutes, canCreate, showTravel, app }),
      ...createRoutesFrom(allowedRoutes),
    ];
  }, [allowedRoutes, user, snapshot, ready, mode, isSuper, showTravel, app]);

  const openPalette = useCallback(() => setPaletteOpen(true), []);
  const closePalette = useCallback(() => setPaletteOpen(false), []);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <ActiveAppContext.Provider value={appContext}>
    <Box sx={{ height: "100vh", minHeight: 560, display: "flex", flexDirection: "column", overflow: "hidden", bgcolor: t.bg }}>
      <HeaderV2
        onOpenPalette={openPalette}
        onOpenNav={() => setNavOpen(true)}
        narrow={narrow}
        createRoutes={createRoutes}
        seepInHeader={!bar.visible}
        softphone={bar}
      />
      <Box sx={{ flex: 1, minHeight: 0, display: "flex", overflow: "hidden" }}>
        {narrow ? (
          <Drawer open={navOpen} onClose={() => setNavOpen(false)} PaperProps={{ sx: { width: 300 } }}>
            <SidebarV2 sidenavRoutes={sidenavRoutes} mobile onNavigate={() => setNavOpen(false)} />
          </Drawer>
        ) : (
          <SidebarV2 sidenavRoutes={sidenavRoutes} />
        )}
        <Box
          component="main"
          id="v2-main"
          ref={mainRef}
          tabIndex={-1}
          sx={{
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            overflowY: "auto",
            boxSizing: "border-box",
            p: narrow ? "16px 14px 32px" : "20px 28px 40px",
            outline: "none",
            // Full-height pages size themselves with scaledVh(); this makes
            // them fit inside <main> (header 56 + vertical padding) instead
            // of the whole window, so they do not get a second scrollbar.
            "--v2-frame-offset": `${(narrow ? 104 : 116) + (bar.visible ? 52 : 0)}px`,
          }}
        >
          <ScrollContainerProvider value={mainRef}>
            {isSettingsPath(pathname) ? <SettingsLayoutV2>{children}</SettingsLayoutV2> : children}
          </ScrollContainerProvider>
        </Box>
      </Box>
      {bar.visible && <SoftphoneBar canHide={bar.canHide} onHide={() => bar.setHidden(true)} />}
      <CommandPalette open={paletteOpen} onClose={closePalette} pages={pages} />
    </Box>
    </ActiveAppContext.Provider>
  );
}

AppFrame.propTypes = {
  sidenavRoutes: PropTypes.arrayOf(PropTypes.object).isRequired,
  allowedRoutes: PropTypes.arrayOf(PropTypes.object).isRequired,
  children: PropTypes.node.isRequired,
};
